import { captureException } from "@utils/captureException";
import type { Player } from "@utils/player/createPlayer";
import { usePlayerEngine } from "../../runtime/provider";
import { useMemo } from "react";
import type {
  PlayerQueueType,
  QueueItem,
} from "@utils/player/utils/queue.types";

const REPORT_THROTTLE_MS = 60_000;
const lastReportByKey = new Map<string, number>();

type SwipeListSurface = "feed" | "bar" | "unknown";
type PlayerBlankGateSurface = "feed-screen" | "bar" | "swipe-list";
type PlayerBlankGateReason =
  | "player-store-not-hydrated"
  | "swipe-list-not-hydrated"
  | "active-queue-type-invalid"
  | "home-active-queue-empty"
  | "home-active-current-item-missing"
  | "home-deeplink-handling-stuck"
  | "bar-current-item-missing";

const getQueueItemDebug = (item: QueueItem | null | undefined) => ({
  queueItemId: item?.qid ?? null,
  songId: item?.metadata.id ?? null,
  songTitle: item?.metadata.title ?? null,
  source: item?.source ?? null,
});

export function createPlayerDiagnostics(player: Player) {
  const getQueueSnapshot = () => {
    const state = player.store.getState();
    const foryouCurrent =
      state.foryouQueue?.items[state.foryouQueue.currentIndex];
    const playlistCurrent =
      state.playlistQueue?.items[state.playlistQueue.currentIndex];
    const hydrationFlatDiagnostics =
      player.getPlayerStoreHydrationFlatDiagnostics();

    return {
      hasHydrated: state._hasHydrated,
      activeQueueType: state.activeQueueType,
      status: state.status,
      userIntent: state.userIntent,
      foryouItemCount: state.foryouQueue?.items.length ?? 0,
      foryouCurrentIndex: state.foryouQueue?.currentIndex ?? null,
      foryouCurrentQueueItemId: foryouCurrent?.qid ?? null,
      foryouCurrentSongId: foryouCurrent?.metadata.id ?? null,
      playlistItemCount: state.playlistQueue?.items.length ?? 0,
      playlistCurrentIndex: state.playlistQueue?.currentIndex ?? null,
      playlistCurrentQueueItemId: playlistCurrent?.qid ?? null,
      playlistCurrentSongId: playlistCurrent?.metadata.id ?? null,
      playlistCollectionId: state.playlistQueue?.collection.id ?? null,
      playlistCollectionType: state.playlistQueue?.collection.type ?? null,
      playlistRecommendationCount: (state.playlistQueue?.items ?? []).filter(
        (item) => item.source === "playlist-recommendation",
      ).length,
      paginationUserId: state.paginationUserId,
      lastFetchedSongId: state.lastFetchedSongId,
      lastViewedSongId: state.lastViewedSongId,
      ...hydrationFlatDiagnostics,
      playerStoreHydration: player.getPlayerStoreHydrationDiagnostics(),
    };
  };

  const shouldReport = (key: string) => {
    const now = Date.now();
    const lastReportAt = lastReportByKey.get(key) ?? 0;
    if (lastReportAt > 0 && now - lastReportAt < REPORT_THROTTLE_MS) {
      return false;
    }
    lastReportByKey.set(key, now);
    return true;
  };

  const captureBlankSwipeListSlot = ({
    expectedIndex,
    expectedItem,
    itemCount,
    queueType,
    slotIndex,
    surface = "unknown",
  }: {
    expectedIndex: number;
    expectedItem: QueueItem | null | undefined;
    itemCount: number;
    queueType: PlayerQueueType;
    slotIndex: number;
    surface?: SwipeListSurface;
  }) => {
    if (!shouldReport(`slot:${surface}:${queueType}`)) return;

    captureException(
      new Error(
        `SwipeList slot blank: ${surface} ${queueType} slot has null currentItem`,
      ),
      {
        channel: "error",
        area: "player",
        operation: "swipe-list-slot-blank",
        fingerprint: ["swipe-list-slot-blank", surface, queueType],
        tags: {
          queueType,
          surface,
          slotIndex: String(slotIndex),
        },
        extra: {
          expectedIndex,
          expectedItemExists: Boolean(expectedItem),
          itemCount,
          ...getQueueItemDebug(expectedItem),
          queueSnapshot: getQueueSnapshot(),
        },
      },
    );
  };

  const captureBlankSwipeListState = ({
    activeIndex,
    baseHeight,
    itemCount,
    progressIndex,
    progressValue,
    queueType,
    reason,
    surface = "unknown",
    targetIndex,
    targetOffsetValue,
  }: {
    activeIndex: number | undefined;
    baseHeight: number;
    itemCount: number;
    progressIndex: number | null;
    progressValue: number;
    queueType: PlayerQueueType;
    reason:
      | "active-list-empty"
      | "active-index-invalid"
      | "progress-index-invalid";
    surface?: SwipeListSurface;
    targetIndex: number | null;
    targetOffsetValue: number;
  }) => {
    if (!shouldReport(`state:${surface}:${queueType}:${reason}`)) return;

    captureException(
      new Error(`SwipeList blank state: ${surface} ${queueType} ${reason}`),
      {
        channel: "error",
        area: "player",
        operation: "swipe-list-blank-state",
        fingerprint: ["swipe-list-blank-state", surface, queueType, reason],
        tags: {
          queueType,
          reason,
          surface,
        },
        extra: {
          activeIndex: activeIndex ?? null,
          baseHeight,
          itemCount,
          progressIndex,
          progressValue,
          targetIndex,
          targetOffsetValue,
          queueSnapshot: getQueueSnapshot(),
        },
      },
    );
  };

  const capturePlayerBlankRenderGate = ({
    extra,
    queueType,
    reason,
    surface,
  }: {
    extra?: Record<string, unknown>;
    queueType?: PlayerQueueType | "none";
    reason: PlayerBlankGateReason;
    surface: PlayerBlankGateSurface;
  }) => {
    const resolvedQueueType = queueType ?? "none";
    if (!shouldReport(`gate:${surface}:${reason}:${resolvedQueueType}`)) return;

    const queueSnapshot = getQueueSnapshot();
    const hydrationPhase =
      typeof queueSnapshot.playerStoreHydrationPhase === "string"
        ? queueSnapshot.playerStoreHydrationPhase
        : "unknown";

    captureException(
      new Error(`Player blank render gate v2: ${surface} ${reason}`),
      {
        channel: "error",
        area: "player",
        operation: "player-blank-render-gate-v2",
        fingerprint: [
          "player-blank-render-gate-v2",
          surface,
          reason,
          resolvedQueueType,
        ],
        tags: {
          activeQueueType: queueSnapshot.activeQueueType ?? "null",
          queueType: resolvedQueueType,
          reason,
          surface,
          hydrationPhase,
        },
        extra: {
          ...extra,
          queueSnapshot,
        },
      },
    );
  };

  const resetBlankSwipeListSlotDiagnosticsForTest = () => {
    lastReportByKey.clear();
  };

  return {
    captureBlankSwipeListSlot,
    captureBlankSwipeListState,
    capturePlayerBlankRenderGate,
    resetBlankSwipeListSlotDiagnosticsForTest,
  };
}
export function usePlayerDiagnostics() {
  const player = usePlayerEngine();
  return useMemo(() => createPlayerDiagnostics(player), [player]);
}
